import { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import type { SceneStore } from '@os/doc';
import type { CommentThread } from '../../lib/comment-types';
import { worldToScreen } from '@os/engine';
import type { ResourceRole } from '@os/shared';
import type { CommentRequest, EditorController } from '../editor/controller';
import { anchorAt, anchorPage, pinPosition, threadsOnPage } from './anchor';
import { pageLabel } from '../pages/labels';
import { CommentsButton } from './CommentsButton';
import { CommentsPanel } from './CommentsPanel';
import type { ThreadFilter } from './filters';
import { CommentPins } from './CommentPins';
import { Composer } from './Composer';
import { ThreadPopover } from './ThreadPopover';
import { commentRights, useCommentWrite, useMentionables, useRoomComments, useThreads, type CommentSync } from './useComments';
import { InTopBar } from '../editor/TopBar';
import { useNotifications } from './useNotifications';
import { usePaletteCommands } from '../palette/registry';

export type CommentsProps = {
  boardId: string;
  me: { id: string; name: string };
  role: ResourceRole;
  /** Access through secret links only (never resolves threads). */
  linkOnly: boolean;
  sync: CommentSync | null;
  /** `?thread=` of the board URL: opened once the threads are loaded (ignored when unknown). */
  initialThread?: string | null;
};

type Props = { controller: EditorController; store: SceneStore; comments: CommentsProps; request: CommentRequest | null; onRequestDone: () => void };

/** Pins, the open thread and the new-thread composer of a server board (spec §I-P7). */
export function CommentsLayer({ controller, store, comments, request, onRequestDone }: Props) {
  const { t } = useTranslation();
  const { boardId, me, role, linkOnly, sync, initialThread = null } = comments;
  const rights = commentRights(role, linkOnly);
  const camera = useSyncExternalStore(controller.subscribeCamera, () => controller.camera);
  // Pins show on their page only (P14); an object moving to another page takes its pins along.
  const page = useSyncExternalStore(useCallback((cb: () => void) => store.subscribePage(cb), [store]), () => store.page);
  // Re-sorted only when objects change page (never on a drag frame: pins follow their objects through useObject).
  const [, setMembership] = useState(0);
  useEffect(() => store.subscribeMembership(() => setMembership((n) => n + 1)), [store]);
  const pages = useSyncExternalStore(useCallback((cb: () => void) => store.subscribePages(cb), [store]), () => store.pages());
  const pageOf = (th: CommentThread) => {
    if (pages.length < 2) return null;
    const at = pages.findIndex((p) => p.id === anchorPage(th.anchor, store));
    return pageLabel(t, pages[at]!, at);
  };
  const threads = useThreads(boardId);
  const mentionables = useMentionables(boardId, rights.canComment);
  const write = useCommentWrite(boardId, sync);
  const [openId, setOpenId] = useState<string | null>(null);
  const [panelOpen, setPanelOpen] = useState(false);
  // Board › Comments in the phone's main menu, and the ⌘K palette everywhere (P5f).
  usePaletteCommands(() => [{ id: 'comments', label: t('comments.open'), group: 'board', keywords: ['commentaires', 'threads'], run: () => setPanelOpen((v) => !v) }]);
  const [filter, setFilter] = useState<ThreadFilter>('open');
  /** The last `?thread=` handled: a new one (a notification of this very board) opens again. */
  const deepLinked = useRef<string | null>(null);
  useRoomComments(boardId, sync);
  const notifications = useNotifications();

  useEffect(() => {
    controller.setCommentable(rights.canComment);
    return () => controller.setCommentable(false);
  }, [controller, rights.canComment]);

  const list = threads.data ?? [];
  const worldOf = (th: CommentThread) => pinPosition(th.anchor, 'objectId' in th.anchor ? store.getObject(th.anchor.objectId) : undefined);
  const focus = (th: CommentThread) => {
    const where = anchorPage(th.anchor, store);
    if (where !== store.page) store.setPage(where);
    controller.centerOn(worldOf(th));
    onRequestDone();
    setOpenId(th.id);
  };
  const onPage = threadsOnPage(list, store, page);
  const open = onPage.find((th) => th.id === openId) ?? null;
  const showResolved = panelOpen && (filter === 'resolved' || filter === 'all');
  const shown = onPage.filter((th) => !th.resolvedAt || showResolved || th.id === openId);
  const openAt = open ? worldToScreen(camera, worldOf(open)) : null;
  const people = mentionables.data ?? [];
  const names = new Map(people.map((p) => [p.userId, p.displayName] as const));

  useEffect(() => {
    if (!initialThread || deepLinked.current === initialThread || !threads.data) return;
    const th = threads.data.find((x) => x.id === initialThread);
    if (!th && threads.isFetching) return; // a thread newer than the list: wait for the refetch
    deepLinked.current = initialThread;
    if (th) focus(th);
  });

  const openCount = list.filter((th) => !th.resolvedAt).length;
  // Unread mentions and replies on this board: the badge turns to the accent, and they count as "For me" (P16-3).
  const notified = useMemo(() => new Set((notifications.data ?? []).filter((n) => n.boardId === boardId && !n.readAt).map((n) => n.threadId)), [notifications.data, boardId]);
  const mention = list.some((th) => !th.resolvedAt && notified.has(th.id));
  const button = (docked: boolean) => <CommentsButton count={openCount} mention={mention} open={panelOpen} onToggle={() => setPanelOpen((v) => !v)} docked={docked} />;
  return (
    <>
      {/* Phones too (P16-3): the bar keeps the avatars and the comments with their badge. */}
      <InTopBar slot="comments" fallback={button(false)}>
        {button(true)}
      </InTopBar>
      {panelOpen && (
        <CommentsPanel threads={list} filter={filter} onFilter={setFilter} onSelect={focus} onClose={() => setPanelOpen(false)} me={me} names={names} notified={notified} pageOf={pageOf} />
      )}
      <CommentPins
        threads={shown}
        store={store}
        camera={camera}
        openId={openId}
        onOpen={(id) => {
          onRequestDone();
          setOpenId((cur) => (cur === id ? null : id));
        }}
      />
      {open && openAt && (
        <ThreadPopover thread={open} at={openAt} me={me} rights={rights} mentionables={people} write={write} onClose={() => setOpenId(null)} />
      )}
      {request && rights.canComment && (
        <div
          style={{ left: `${Math.max(8, request.screen.x + 20)}px`, top: `${Math.max(8, request.screen.y - 12)}px` }}
          className="absolute z-30 w-80 max-w-[calc(100%-1rem)] rounded-xl bg-surface p-3 shadow-lg"
        >
          <Composer
            label={t('comments.placeholder')}
            mentionables={people}
            autoFocus
            onCancel={onRequestDone}
            onSubmit={async (body) => {
              const id = await write.mutateAsync({ kind: 'create', anchor: anchorAt(store, request.at, request.objectId), body });
              onRequestDone();
              setOpenId(null);
              return id;
            }}
          />
        </div>
      )}
    </>
  );
}
